import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import {
  CreateDocumentTypeRequest,
  DocumentType,
  DocumentTypeReduced,
  ListDocumentTypesQuery,
  ListDocumentTypesQueryReduced,
  UpdateDocumentTypeRequest,
} from './DocumentTypes.types';

import {
  listDocumentTypes,
  deleteDocumentType,
  createDocumentType,
  updateDocumentType,
  getDocumentType,
  activeDocumentType,
  listDocumentTypesReduced,
  getDocumentTypeReduced,
} from './DocumentTypes.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { message, notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';

export function useDocumentTypes(params?: ListDocumentTypesQuery) {
  const query = useInfiniteQuery(
    ['documentTypes', { ...params }],
    ({ pageParam }) => {
      const req: ListDocumentTypesQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listDocumentTypes(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useDocumentTypesReduced(
  params?: ListDocumentTypesQueryReduced,
  enabled = true,
) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryData([
    'documentTypes/reduced',
    { ...params },
  ]);

  return useInfiniteQuery(
    ['documentTypes/reduced', { ...params }],
    ({ pageParam }) => {
      const req: ListDocumentTypesQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listDocumentTypesReduced(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      cacheTime: 10000,
      enabled: enabled && !cache,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useDocumentType(id?: number) {
  const query = useQuery(
    ['documentType', id],
    () => (id ? getDocumentType(id) : undefined),
    { enabled: !!id },
  );

  useServiceError(query.error);

  return query;
}

export function useDocumentTypeMutation() {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<DocumentType>([
        'documentType/detail',
        id,
      ]);
      if (cache?.id === id) return cache;

      const data = await getDocumentType(id);
      return data;
    },
    {
      onSuccess(data) {
        queryClient.setQueryData(['documentType/detail', data.id], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDocumentTypeReduced() {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<DocumentTypeReduced>([
        'documentType/detail/reduced',
        id,
      ]);

      if (cache?.id === id) return cache;

      const data = await getDocumentTypeReduced(id);
      return data;
    },
    {
      onSuccess(data) {
        queryClient.setQueryData(
          ['documentType/detail/reduced', data.id],
          data,
        );
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCreateDocumentType() {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: CreateDocumentTypeRequest) => createDocumentType(obj),
    {
      async onSuccess(data) {
        await queryClient.resetQueries(['documentTypes/reduced']);
        await queryClient.invalidateQueries(['documentType', data.id]);

        await queryClient.invalidateQueries(['documentTypeRates', data.id]);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateDocumentType() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: UpdateDocumentTypeRequest) => updateDocumentType(obj),
    {
      async onSuccess(_, data) {
        notification.success({
          message: intl.formatMessage({
            id: 'documentTypes.update.notification.success',
          }),
          description: intl.formatMessage({
            id: 'documentTypes.update.notification.success.description',
          }),
        });

        await queryClient.invalidateQueries(['documentType', data.id]);
        queryClient.removeQueries(['documentTypes/reduced']);
        queryClient.removeQueries(['documentType/detail', data.id]);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useActiveDocumentType() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation((id: number) => activeDocumentType(id), {
    async onSuccess(_, id) {
      message.success(intl({ id: 'documentType.notification.update.status' }));

      await queryClient.invalidateQueries(['documentType', id]);
    },
  });
  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteDocumentType() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation((id: number) => deleteDocumentType(id), {
    async onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'documentTypes.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'documentTypes.remove.notification.success.description',
        }),
      });

      await queryClient.resetQueries(['documentTypes/reduced']);
      await queryClient.invalidateQueries(['documentTypes']);
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Document Type cannot be deleted due to relation.')
              ? 'Não é possível excluir o tipo de documento, pois ele tem vínculo com outro módulo'
              : err,
          ),
      });
    },
  });

  return mutation;
}
